// Offline-resilient score submission queue.
// Persists to localStorage so a tab refresh / app restart while offline doesn't lose strokes.
// Replays FIFO on `online` event and on module load.

import { api } from "./api";

const QUEUE_KEY = "fairway:offline-score-queue";
const LISTENERS = new Set();

function readQueue() {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function writeQueue(items) {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(items));
  } catch {}
  LISTENERS.forEach((fn) => {
    try { fn(items.length); } catch {}
  });
}

export function getQueueLength() {
  return readQueue().length;
}

export function subscribe(listener) {
  LISTENERS.add(listener);
  listener(getQueueLength());
  return () => LISTENERS.delete(listener);
}

function isNetworkError(err) {
  // axios sets err.response when a server replied. Missing response = transport-level failure.
  return !err?.response;
}

/**
 * Send a hole score. If we're offline or the request fails at transport-level, queue it.
 * Returns { data, queued } — data is the server's Round when not queued, else null.
 * Caller can optimistically update local round state when queued.
 */
export async function postScoreWithQueue(roundId, body) {
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    pushToQueue(roundId, body);
    return { data: null, queued: true };
  }
  try {
    const { data } = await api.post(`/rounds/${roundId}/score`, body);
    return { data, queued: false };
  } catch (err) {
    if (isNetworkError(err)) {
      pushToQueue(roundId, body);
      return { data: null, queued: true };
    }
    throw err;
  }
}

function pushToQueue(roundId, body) {
  const q = readQueue();
  // Collapse: if last queued item is the same round + hole + player, replace it (we only care about the latest score for that hole).
  const last = q[q.length - 1];
  const sameTarget =
    last &&
    last.roundId === roundId &&
    last.body.hole_number === body.hole_number &&
    (last.body.player_index || 0) === (body.player_index || 0);
  if (sameTarget) {
    q[q.length - 1] = { roundId, body, ts: Date.now() };
  } else {
    q.push({ roundId, body, ts: Date.now() });
  }
  writeQueue(q);
}

let flushing = false;
export async function flushQueue() {
  if (flushing) return;
  flushing = true;
  try {
    let q = readQueue();
    while (q.length > 0) {
      if (typeof navigator !== "undefined" && navigator.onLine === false) break;
      const item = q[0];
      try {
        await api.post(`/rounds/${item.roundId}/score`, item.body);
        q = q.slice(1);
        writeQueue(q);
      } catch (err) {
        if (isNetworkError(err)) {
          // Still offline-ish — bail; we'll try again on the next 'online' event.
          break;
        }
        // 4xx (e.g. round missing, validation). Drop the bad item rather than blocking the queue forever.
        q = q.slice(1);
        writeQueue(q);
      }
    }
  } finally {
    flushing = false;
  }
}

if (typeof window !== "undefined") {
  window.addEventListener("online", flushQueue);
  // Attempt a flush on module load (covers the "user opened the app after coming back online" case).
  setTimeout(flushQueue, 1500);
}
